"use client";

import React, { useState } from "react";
import { FaPlus, FaMinus } from "react-icons/fa";

import { AnimatePresence, motion } from "framer-motion";

interface FaqItem {
    id: string;
    question: string;
    answer: string;
}

const faqs: FaqItem[] = [
    {
        id: "faq-1",
        question: "What does the Loyalty Loan Program mean for me?",
        answer: "The Loyalty Loan Program means that you will never have to call about another advertisement you receive in the mail or wonder if you qualify for a lower rate in the future. We do the work for you! We will monitor your interest rate during the servicing of your loan and, should market conditions improve, we will gladly offer you a lower rate with a lower payment.",
    },
    {
        id: "faq-2",
        question: "This sounds too good to be true. Why does ClearPath Lending offer the Loyalty Program?",
        answer: "It’s quite simple. If rates go down after we close your loan, we understand that you may look to refinance elsewhere and possibly lower your rate. For that reason, we make sure to contact you before you contact someone else. The main objective of the Loyalty Program is to keep our relationship with you and help you with all your future mortgage-related needs. The last thing we want is to lose your valuable business to a competitor. That is why we put together one of the most comprehensive and desired mortgage loan programs in the industry. We call it the Loyalty Loan Program.",
    },
    {
        id: "faq-3",
        question: "How long do I have to wait before my eligibility for the Loyalty Program begins?",
        answer: "Typically, you have to make at least 3 payments on time with the servicer of your loan. However, there are times when you must make 6 payments, depending on the loan program you are currently enrolled in.",
    },
    {
        id: "faq-4",
        question: "Can I call the Loyalty Department at ClearPath Lending to see if I qualify for a rate reduction?",
        answer: "You can absolutely call us to see if you are eligible for rate reduction under the Loyalty Program. Please reference your eligibility number which can be found on your loyalty certificate provided in your kit when calling us at 1-800-LOYALTY (569-2589). You can also email Loyalty@clearpathlending.com for any inquiries. Once again, please be sure to reference your eligibility number if you choose email over phone contact.",
    },
    {
        id: "faq-5",
        question:
            "My current servicer offered me a lower rate with no costs. Is this the same offer as the Loyalty Program?",
        answer: "It is important to note that any offer you receive from your current servicer is from their retail department, which we have no affiliation with. The offer you receive from them is not part of our Loyalty Program and in some cases, the terms of their offer may be less favorable than our Loyalty Program offer.",
    },
    {
        id: "faq-6",
        question:
            "ClearPath Lending transferred the servicing of my loan to another lender, does this mean that I am no longer eligible for the Loyalty Program?",
        answer: "ClearPath Lending currently does not service loans. Regardless of who we transfer the servicing of your loan to, your eligibility for the Loyalty Program will stay intact with ClearPath Lending.",
    },
    {
        id: "faq-7",
        question: "Are there any costs with the Loyalty Program?",
        answer: "No, there are no costs associated with the Loyalty Program. However, in some cases, you may be eligible for multiple refinance options. A no discount point option will always be available. Other options with even lower interest rates may also be available, but discount points or other costs may apply.",
    },
    {
        id: "faq-8",
        question: "Is my eligibility under the Loyalty Program a one-time usage?",
        answer: "No, when you closed your first loan with ClearPath Lending, you were automatically enrolled in the Loyalty Program. However, each time you refinance your mortgage through the Loyalty Program, you will have to complete a minimum of 3-6 payments on the new mortgage, depending on the loan type, for your eligibility to begin once again.",
    },
];

const LoyaltyFAQs = () => {
    const [openFaqIds, setOpenFaqIds] = useState<string[]>([]);

    const toggleFaq = (id: string) => {
        setOpenFaqIds((prev) => (prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]));
    };

    // Split into left/right columns
    const leftFaqs = faqs.slice(0, faqs.length / 2);
    const rightFaqs = faqs.slice(faqs.length / 2);

    const renderFaqItem = (faq: FaqItem) => (
        <div
            key={faq.id}
            className="border rounded-lg overflow-hidden 2xl:w-[585px] "
            style={{ borderColor: "rgb(18 117 186 / 0.5)" }}
        >
            <button
                type="button"
                onClick={() => toggleFaq(faq.id)}
                aria-expanded={openFaqIds.includes(faq.id)}
                aria-controls={`${faq.id}-panel`}
                className={`w-full text-left px-4 py-3 flex justify-between items-center transition-colors duration-300 2xl:w-[585px] 2xl:!h-[70px] ${
                    openFaqIds.includes(faq.id)
                        ? "bg-blue text-white hover:bg-blue hover:text-white"
                        : "bg-extraLightGrey text-lightGrey hover:bg-blue hover:text-white"
                }`}
            >
                <span className="font-medium text-[15px] transition-colors duration-300 w-[257px] sm:w-full 2xl:w-[585px] 2xl:h-[66px] 2xl:flex 2xl:items-center 2xl:w-[529px]">
                    {faq.question}
                </span>

                <span
                    className={`text-bgblue w-3 h-3 flex items-center justify-center transition-transform duration-300 ${
                        openFaqIds.includes(faq.id) ? "rotate-180" : "rotate-0"
                    }`}
                >
                    {openFaqIds.includes(faq.id) ? <FaMinus className="w-3 h-3" /> : <FaPlus className="w-3 h-3" />}
                </span>
            </button>

            <AnimatePresence initial={false}>
                {openFaqIds.includes(faq.id) && (
                    <motion.div
                        id={`${faq.id}-panel`}
                        role="region"
                        aria-labelledby={`${faq.id}-label`}
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="px-4 bg-white border-t overflow-hidden"
                    >
                        <p className="text-black w-[258px] sm:w-full text-[15px] 2xl:w-[543px] py-3 2xl:py-4">{faq.answer}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    return (
        <section className="py-8 px-2 md:px-6 lg:px-8 mx-auto w-[300px] sm:w-full pt-[45px] mt-28 2xl:w-[1230px] 2xl:pl-[100px] 2xl:pt-12">
            <h2 className="text-center font-bold text-[30px] mb-6 2xl:w-[1200px] text-darkBlue">FAQS</h2>

            <div className="flex flex-col gap-4 2xl:flex-row 2xl:gap-x-5 2xl:gap-y-4 2xl:space-y-0 2xl:w-[1230px] 2xl:items-start">
                <div className="flex flex-col gap-1 w-full 2xl:w-[585px]">{leftFaqs.map(renderFaqItem)}</div>
                <div className="flex flex-col gap-1 w-full 2xl:w-[585px]">{rightFaqs.map(renderFaqItem)}</div>
            </div>
        </section>
    );
};

export default LoyaltyFAQs;
